// AdminConfigPanel — edit SystemConfig (tokens-per-dollar, provider markup,
// ElevenLabs voices per round, default interview modality).
//
// Routes used:
//   GET  /api/admin/config
//   PUT  /api/admin/config   body: { tokensPerDollar, markup, voices, defaultModality }

import React, { useEffect, useState } from "react";
import { Save, RefreshCw, Loader2 } from "lucide-react";
import api from "../../services/api";
import { toast } from "react-toastify";

export default function AdminConfigPanel() {
  const [cfg, setCfg]         = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/config");
      setCfg(r.data);
    } catch (e) { toast.error("Failed to load config"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        tokensPerDollar: cfg.tokensPerDollar,
        markup:          cfg.markup,
        voices:          cfg.voices,
        defaultModality: cfg.defaultModality,
      };
      const r = await api.put("/admin/config", payload);
      setCfg(r.data);
      toast.success("Config saved");
    } catch (e) {
      toast.error(e.response?.data?.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="p-8 flex items-center gap-2 text-slate-500"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>;
  }
  if (!cfg) return <div className="p-8 text-rose-600">Config unavailable.</div>;

  const set = (path, value) => {
    setCfg(prev => {
      const next = { ...prev };
      const keys = path.split(".");
      let cur = next;
      for (let i = 0; i < keys.length - 1; i++) {
        cur[keys[i]] = { ...(cur[keys[i]] || {}) };
        cur = cur[keys[i]];
      }
      cur[keys.at(-1)] = value;
      return next;
    });
  };

  return (
    <div className="space-y-6 max-w-3xl">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-slate-900">System Config</h2>
          <p className="text-sm text-slate-500">Token economy + ElevenLabs voices. Applies to all jobs that don't override at the job level.</p>
        </div>
        <div className="flex gap-2">
          <button onClick={load} className="px-3 py-2 text-sm text-slate-600 hover:text-slate-900 flex items-center gap-2">
            <RefreshCw className="w-4 h-4" /> Reload
          </button>
          <button onClick={save} disabled={saving} className="px-4 py-2 bg-orange-500 text-white rounded-md hover:bg-orange-600 disabled:opacity-40 flex items-center gap-2">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Save
          </button>
        </div>
      </div>

      {/* Token economy */}
      <section className="bg-white border border-slate-200 rounded-lg p-6 space-y-4">
        <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-600">Token economy</h3>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Tokens per US Dollar</label>
            <input
              type="number" min={1} step={1}
              value={cfg.tokensPerDollar ?? 500}
              onChange={(e) => set("tokensPerDollar", Number(e.target.value))}
              className="w-full p-2 border border-slate-300 rounded-md focus:ring-orange-500 focus:border-orange-500"
            />
            <p className="text-xs text-slate-500 mt-1">e.g. 500 means $1 of AI spend deducts 500 wallet tokens from the user.</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Provider markup</label>
            <input
              type="number" min={1} step={0.05}
              value={cfg.markup ?? 1.0}
              onChange={(e) => set("markup", Number(e.target.value))}
              className="w-full p-2 border border-slate-300 rounded-md focus:ring-orange-500 focus:border-orange-500"
            />
            <p className="text-xs text-slate-500 mt-1">1.0 = pass-through. 1.5 = 50% markup on raw AI cost.</p>
          </div>
        </div>
      </section>

      {/* Modality */}
      <section className="bg-white border border-slate-200 rounded-lg p-6 space-y-4">
        <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-600">Default interview modality</h3>
        <div className="flex gap-3">
          {["audio", "text"].map(m => (
            <button
              key={m}
              onClick={() => set("defaultModality", m)}
              className={`px-4 py-2 rounded-md border-2 text-sm font-medium capitalize ${
                cfg.defaultModality === m ? "border-orange-500 bg-orange-50 text-orange-700" : "border-slate-200 text-slate-600 hover:border-slate-300"
              }`}
            >{m}</button>
          ))}
        </div>
        <p className="text-xs text-slate-500">Applies when a job doesn't set its own modality.</p>
      </section>

      {/* Voices */}
      <section className="bg-white border border-slate-200 rounded-lg p-6 space-y-4">
        <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-600">ElevenLabs voice IDs (English agents)</h3>
        <div className="grid grid-cols-1 gap-3">
          {[
            { key: "preScreening",     label: "Pre-screening (Mira)" },
            { key: "psychoanalytical", label: "Psychoanalytical (Dr. Patel)" },
            { key: "finalInterview",   label: "Final interview (Senior Panel)" },
          ].map(({ key, label }) => (
            <div key={key} className="flex items-center gap-3">
              <label className="w-56 text-sm text-slate-700">{label}</label>
              <input
                value={cfg.voices?.[key] || ""}
                onChange={(e) => set(`voices.${key}`, e.target.value)}
                placeholder="ElevenLabs voice_id"
                className="flex-1 p-2 border border-slate-300 rounded-md font-mono text-xs"
              />
            </div>
          ))}
        </div>
        <p className="text-xs text-slate-500">
          Non-English interviews auto-pick a multilingual voice from a built-in mapping.
        </p>
      </section>
    </div>
  );
}
